import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  PlusIcon,
  TrashIcon,
  FolderIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline'
import {
  SkillApi,
  CourseSkillLink,
  SkillPack,
  SkillTreeNode,
} from '../../services/skillApi'
import SkillTree from './SkillTree'

interface CourseSkillAttachProps {
  courseId: number
  expanded?: boolean
  onToggleExpanded?: () => void
  fillHeight?: boolean
}

const CourseSkillAttach = ({
  courseId,
  expanded = true,
  onToggleExpanded,
  fillHeight = false,
}: CourseSkillAttachProps) => {
  const [links, setLinks] = useState<CourseSkillLink[]>([])
  const [loading, setLoading] = useState(true)
  const [showPicker, setShowPicker] = useState(false)
  const [packs, setPacks] = useState<(SkillPack & { tree: SkillTreeNode[] })[]>([])
  const [selectedPackId, setSelectedPackId] = useState<number | null>(null)
  const [selectedFolder, setSelectedFolder] = useState<SkillTreeNode | null>(null)
  const [saving, setSaving] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [deleting, setDeleting] = useState(false)

  const loadLinks = async () => {
    try {
      setLoading(true)
      const data = await SkillApi.listCourseSkillLinks(courseId, true)
      setLinks(data)
      setSelectedIds(new Set())
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được skill đính kèm')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadLinks()
  }, [courseId])

  const allSelected = useMemo(
    () => links.length > 0 && selectedIds.size === links.length,
    [links, selectedIds]
  )
  const someSelected = selectedIds.size > 0

  const openPicker = async () => {
    try {
      setShowPicker(true)
      const data = await SkillApi.listPacksPicker()
      setPacks(data)
      setSelectedPackId(data[0]?.id ?? null)
      setSelectedFolder(null)
    } catch (e: any) {
      toast.error(e?.message || 'Không tải được thư viện skill')
      setShowPicker(false)
    }
  }

  const selectedPack = packs.find((p) => p.id === selectedPackId) || null

  /** Attach selected folder as root — students browse full nested tree under it. */
  const handleAttachFolder = async () => {
    if (!selectedFolder) {
      toast.error('Chọn một thư mục / file')
      return
    }
    try {
      setSaving(true)
      if (selectedFolder.nodeType === 'FOLDER') {
        await SkillApi.addCourseSkillLinks(courseId, { parentNodeId: selectedFolder.id })
      } else {
        await SkillApi.addCourseSkillLinks(courseId, { nodeIds: [selectedFolder.id] })
      }
      toast.success(
        selectedFolder.nodeType === 'FOLDER'
          ? 'Đã gắn thư mục — học viên sẽ thấy toàn bộ cây con và file bên trong'
          : 'Đã gắn file'
      )
      setShowPicker(false)
      await loadLinks()
    } catch (e: any) {
      toast.error(e?.message || 'Gắn skill thất bại')
    } finally {
      setSaving(false)
    }
  }

  const toggleOne = (linkId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(linkId)) next.delete(linkId)
      else next.add(linkId)
      return next
    })
  }

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(links.map((l) => l.id)))
    }
  }

  const handleDeleteSelected = async () => {
    if (!someSelected) return
    const count = selectedIds.size
    if (
      !window.confirm(
        count === links.length
          ? `Xóa tất cả ${count} liên kết skill của khóa học này?`
          : `Xóa ${count} liên kết skill đã chọn?`
      )
    ) {
      return
    }
    try {
      setDeleting(true)
      const deleted =
        count === links.length
          ? await SkillApi.bulkDeleteCourseSkillLinks(courseId, { all: true })
          : await SkillApi.bulkDeleteCourseSkillLinks(courseId, {
              linkIds: [...selectedIds],
            })
      toast.success(`Đã xóa ${deleted} liên kết`)
      await loadLinks()
    } catch (e: any) {
      toast.error(e?.message || 'Xóa thất bại')
    } finally {
      setDeleting(false)
    }
  }

  const handleDeleteAll = async () => {
    if (links.length === 0) return
    if (!window.confirm(`Xóa tất cả ${links.length} liên kết skill của khóa học này?`)) return
    try {
      setDeleting(true)
      const deleted = await SkillApi.bulkDeleteCourseSkillLinks(courseId, { all: true })
      toast.success(`Đã xóa ${deleted} liên kết`)
      await loadLinks()
    } catch (e: any) {
      toast.error(e?.message || 'Xóa thất bại')
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className={`flex min-h-0 flex-col ${fillHeight ? 'h-full' : ''}`}>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-m2-outline-variant px-4 py-3">
        <button
          type="button"
          onClick={onToggleExpanded}
          className="flex min-w-0 items-center gap-2 text-left hover:text-m2-primary"
          disabled={!onToggleExpanded}
        >
          {onToggleExpanded &&
            (expanded ? (
              <ChevronUpIcon className="h-5 w-5 shrink-0 text-m2-on-surface-variant" />
            ) : (
              <ChevronDownIcon className="h-5 w-5 shrink-0 text-m2-on-surface-variant" />
            ))}
          <div className="min-w-0 text-left">
            <h3 className="text-lg font-bold text-m2-on-surface">Bộ skill đính kèm</h3>
            {expanded && (
              <p className="text-sm text-m2-on-surface-variant">
                Chọn thư mục cha để gắn — học viên xem theo cây thư mục (gồm mọi thư mục con và file).
              </p>
            )}
            {!expanded && links.length > 0 && (
              <p className="text-xs text-m2-on-surface-variant">{links.length} liên kết</p>
            )}
          </div>
        </button>
        <div className="flex flex-wrap items-center gap-2">
          {onToggleExpanded && (
            <button
              type="button"
              onClick={onToggleExpanded}
              className="rounded-md border border-m2-outline px-2.5 py-1.5 text-xs text-m2-on-surface-variant hover:bg-m2-surface-high"
            >
              {expanded ? 'Thu gọn' : 'Mở rộng'}
            </button>
          )}
          {expanded && links.length > 0 && (
            <button
              type="button"
              onClick={handleDeleteAll}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100 disabled:opacity-50"
            >
              <TrashIcon className="h-4 w-4" />
              Xóa tất cả
            </button>
          )}
          {expanded && (
            <button
              type="button"
              onClick={openPicker}
              className="inline-flex items-center gap-1.5 rounded-md border border-m2-primary/30 bg-m2-primary/10 px-4 py-2 text-sm font-medium text-m2-primary hover:bg-m2-primary/15"
            >
              <PlusIcon className="h-4 w-4" />
              Gắn skill
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className={`min-h-0 p-4 ${fillHeight ? 'flex-1 overflow-y-auto' : ''}`}>
          {loading ? (
            <p className="text-sm text-m2-on-surface-variant">Đang tải...</p>
          ) : links.length === 0 ? (
            <div className="rounded-xl border border-dashed border-m2-outline-variant bg-m2-surface-low p-8 text-center text-m2-on-surface-variant">
              Chưa gắn skill nào vào khóa học
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-m2-outline-variant bg-m2-surface-low">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-m2-outline-variant bg-m2-surface-high px-4 py-2.5">
                <label className="flex cursor-pointer items-center gap-2 text-sm text-m2-on-surface">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected && !allSelected
                    }}
                    onChange={toggleAll}
                  />
                  <span>
                    {allSelected
                      ? `Đã chọn tất cả (${links.length})`
                      : someSelected
                        ? `Đã chọn ${selectedIds.size}/${links.length}`
                        : `Chọn tất cả (${links.length})`}
                  </span>
                </label>
                <button
                  type="button"
                  disabled={!someSelected || deleting}
                  onClick={handleDeleteSelected}
                  className="inline-flex items-center gap-1.5 rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <TrashIcon className="h-4 w-4" />
                  Xóa đã chọn
                  {someSelected ? ` (${selectedIds.size})` : ''}
                </button>
              </div>
              <ul className="divide-y divide-m2-outline-variant">
                {links.map((link) => {
                  const checked = selectedIds.has(link.id)
                  return (
                    <li
                      key={link.id}
                      className={`flex items-center gap-3 px-4 py-3 ${
                        checked ? 'bg-m2-primary/5' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="shrink-0"
                        checked={checked}
                        onChange={() => toggleOne(link.id)}
                        aria-label={`Chọn ${link.title || link.nodeName}`}
                      />
                      <FolderIcon className="h-5 w-5 shrink-0 text-amber-600" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-m2-on-surface">
                          {link.title || link.nodeName}
                        </div>
                        <div className="truncate text-xs text-m2-on-surface-variant">
                          {link.packName} · {link.relativePath}
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-m2-surface-container shadow-xl">
            <div className="border-b border-m2-outline-variant px-5 py-4">
              <h2 className="text-lg font-semibold">Chọn thư mục skill để gắn</h2>
              <p className="mt-1 text-xs text-m2-on-surface-variant">
                Chọn thư mục cha — học viên sẽ duyệt cả cây con và mở được mọi file bên trong.
              </p>
            </div>
            <div className="grid min-h-0 flex-1 gap-3 overflow-hidden p-4 md:grid-cols-[200px_1fr]">
              <div className="max-h-[50vh] overflow-y-auto rounded border border-m2-outline-variant">
                {packs.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`block w-full truncate px-3 py-2 text-left text-sm hover:bg-m2-surface-high ${
                      selectedPackId === p.id ? 'bg-m2-primary/10 text-m2-primary' : ''
                    }`}
                    onClick={() => {
                      setSelectedPackId(p.id)
                      setSelectedFolder(null)
                    }}
                  >
                    {p.name}
                  </button>
                ))}
              </div>
              <div className="max-h-[50vh] overflow-y-auto rounded border border-m2-outline-variant">
                {selectedPack ? (
                  <SkillTree
                    nodes={selectedPack.tree || []}
                    selectedId={selectedFolder?.id}
                    onSelectFile={(n) => setSelectedFolder(n)}
                    onSelectFolder={(n) => setSelectedFolder(n)}
                  />
                ) : (
                  <p className="p-4 text-sm text-m2-on-surface-variant">Chọn pack</p>
                )}
              </div>
            </div>
            {selectedFolder && (
              <div className="border-t border-m2-outline-variant px-5 py-2 text-sm text-m2-on-surface">
                Đang chọn: <strong>{selectedFolder.name}</strong>{' '}
                <span className="text-xs text-m2-on-surface-variant">
                  ({selectedFolder.relativePath})
                </span>
              </div>
            )}
            <div className="flex flex-wrap justify-end gap-2 border-t border-m2-outline-variant px-5 py-4">
              <button
                type="button"
                className="rounded-md border border-m2-outline px-4 py-2 text-sm"
                onClick={() => setShowPicker(false)}
                disabled={saving}
              >
                Hủy
              </button>
              <button
                type="button"
                className="rounded-md bg-m2-primary px-4 py-2 text-sm text-m2-on-primary disabled:opacity-50"
                disabled={saving || !selectedFolder}
                onClick={handleAttachFolder}
              >
                {selectedFolder?.nodeType === 'FOLDER'
                  ? 'Gắn thư mục này (kèm cây con)'
                  : 'Gắn file này'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CourseSkillAttach
